import { MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { IconEdit, IconExclamationCircle } from '@rossum/ui/icons/tabler';
import {
  IconButton,
  MenuItem,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { retrieveColumnLabel } from '../core';

const editButtonClassName = 'view-menu-item-edit-button';

export const SavedViewMenuItem = ({
  selected,
  name,
  onClick,
  missingSchemaColumns,
  onEdit,
  queueCount,
}: {
  missingSchemaColumns?: string[];
  selected: boolean;
  name: string;
  onEdit: () => void;
  onClick: () => void;
  queueCount: number;
}) => {
  const intl = useIntl();

  const hasMissingColumn =
    missingSchemaColumns && missingSchemaColumns.length > 0;

  const handlePropagation =
    (callback: () => void) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      callback();
    };

  return (
    <MenuItem
      selected={selected}
      onClick={onClick}
      data-cy={`saved-view-item-${name}`}
      sx={{
        '&:hover': {
          [`& .${editButtonClassName}`]: { visibility: 'visible' },
        },
      }}
    >
      <Stack sx={{ gap: 1, alignItems: 'center', width: 1 }} direction="row">
        <Typography
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {name}
        </Typography>

        <Stack direction="row" sx={{ ml: 'auto' }} spacing={1} useFlexGap>
          {hasMissingColumn ? (
            <Tooltip
              placement="top"
              title={
                hasMissingColumn ? (
                  <Stack sx={{ fontSize: t => t.typography.caption }}>
                    <Typography mb={1} fontSize="inherit">
                      {intl.formatMessage(
                        {
                          id: 'containers.savedViews.tooltips.missingColumn',
                        },
                        {
                          columns: missingSchemaColumns
                            .map(retrieveColumnLabel)
                            .join(', '),
                        }
                      )}
                    </Typography>
                  </Stack>
                ) : null
              }
            >
              <SvgIcon
                color="warning"
                fontSize="medium"
                data-cy="saved-view-missing-column-indicator"
              >
                <IconExclamationCircle />
              </SvgIcon>
            </Tooltip>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {queueCount !== 0
              ? queueCount
              : intl.formatMessage({
                  id: 'containers.savedViews.allQueuesCount',
                })}
          </Typography>

          <IconButton
            size="small"
            onClick={handlePropagation(onEdit)}
            data-cy={`saved-view-edit-action-${name}`}
            className={editButtonClassName}
            sx={{ visibility: 'hidden' }}
          >
            <SvgIcon
              fontSize="medium"
              sx={{ color: t => t.palette.action.active }}
            >
              <IconEdit />
            </SvgIcon>
          </IconButton>
        </Stack>
      </Stack>
    </MenuItem>
  );
};
